import { GridColumnInputEditor } from '@Common/Controls/SimpleGridLight';
import { Input } from '@Common/Controls/Input';
import { IGridLayoutParameters } from '@Common/Controls/GridLayout';
import { BaseCompositeControl } from '@Common/Controls/BaseCompositeControl';
import { GridControlCE } from '@Common/Controls/GridControlCE';
import { enumerable } from '@Common/Core/CoreDecorator';

export class EmailsControlParameters {
    public readonly delimiter = ';';
    public readonly title = strings.emailsControl.addressColumnName;
}

export class EmailsControl extends BaseCompositeControl {
    private textEmails: Input;
    private listEmails: GridControlCE;
    private viewModel: EmailsControlViewModel;

    constructor(private readonly parameters: Partial<EmailsControlParameters> = {}) {
        super();
        this.parameters = { ...new EmailsControlParameters(), ...this.parameters };
        this.createRoot();
    }

    public init(emails: string[]): void {
        this.viewModel.init(emails);
    }

    @enumerable
    public get textView(): string {
        return this.viewModel.textView;
    }

    public set textView(str: string) {
        this.viewModel.textView = str;
    }

    protected initLayout(): IGridLayoutParameters {
        this.textEmails = new Input({});
        this.listEmails = new GridControlCE({
            columns: [
                {
                    dataIndex: 'email',
                    title: this.parameters.title,
                    editor: new GridColumnInputEditor({})
                }
            ]
        });

        this.viewModel = new EmailsControlViewModel((this as any) as IView, this.parameters.delimiter);

        this.textEmails.onChange(() => {
            this.viewModel.textView = this.textEmails.getValue();
        });

        this.listEmails.onBeforeAddRow(arg => {
            arg.newData = this.viewModel.createNewData();
        });

        this.listEmails.onBeforeDeleteRow(arg => {
            const data: EmailListData = arg.row.getData();
            data.remove();
            arg.cancel = true;
        });
        return {
            rows: [{ style: { height: 'auto' } }, { style: { height: 'auto' } }, { style: { height: '*' } }],
            columns: [{ style: { width: '*' } }],
            cells: [
                { rowIndex: 0, columnIndex: 0, content: strings.emailsControl.inputName },
                { rowIndex: 1, columnIndex: 0, content: this.textEmails },
                { rowIndex: 2, columnIndex: 0, content: this.listEmails }
            ]
        };
    }

    private updateText(str: string): void {
        this.textEmails.setValue(str);
    }

    private updateList(list: EmailListData[]): void {
        this.listEmails.setData(list);
    }
}

class EmailListData {
    constructor(private index: number, private own: EmailsControlViewModel) {}

    @enumerable
    public get email(): string {
        return this.own.getEmailByIndex(this.index);
    }

    public set email(val: string) {
        this.own.setEmailByIndex(this.index, val);
    }

    @enumerable
    public get isValid(): boolean {
        return true;
    }

    public remove(): void {
        this.own.remove(this.index);
    }
}

interface IView {
    updateText(str: string): void;

    updateList(list: EmailListData[]): void;
}

class EmailsControlViewModel {
    private emailsList: string[] = [];

    constructor(private readonly view: IView, private readonly delimiter: string = ';') {}

    @enumerable
    public get textView(): string {
        return this.emailsList.join(this.delimiter);
    }

    public set textView(val: string) {
        const newEmails = val.split(this.delimiter);
        this.emailsList.length = 0;
        this.emailsList.push(...newEmails);
        this.updateList();
    }

    public init(emailsList: string[]): void {
        this.emailsList = emailsList;
        this.updateText();
        this.updateList();
    }

    public getEmailByIndex(index: number): string {
        return this.emailsList[index];
    }

    public setEmailByIndex(index: number, value: string): void {
        this.emailsList[index] = value;
        this.updateText();
    }

    public remove(index: number): void {
        this.emailsList.splice(index, 1);
        this.updateText();
        this.updateList();
    }

    public createNewData(): EmailListData {
        const newIndex = this.emailsList.push('') - 1;
        this.updateText();
        return new EmailListData(newIndex, this);
    }

    private updateList(): void {
        const newData = this.emailsList.map((email, index) => new EmailListData(index, this));
        this.view.updateList(newData);
    }

    private updateText(): void {
        this.view.updateText(this.textView);
    }
}

namespace manualTests {
    export function emailControls(root: JQuery): void {
        const ctr = new EmailsControl();
        ctr.getControl().appendTo(root);

        ctr.adjust(300, 500);

        ctr.init(['1', '3']);
    }
}
